import { useState, useCallback, useEffect, useRef } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import { AnalyticsEvent } from '../constants/analyticsEvents';
import { useAnalytics } from './useAnalytics';
import { useUserContext } from '../context/use-user-context';
import { Featureflag } from '../constants/featureFlags';

const STORAGE_KEY = 'superbar_exit_intent_shown';

const hasShownThisSession = (): boolean => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const markAsShown = (): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // Ignore storage errors
  }
};

interface UseExitIntentOptions {
  enabled?: boolean;
  question?: string;
  onExitIntent?: () => void;
}

interface UseExitIntentReturn {
  isTriggered: boolean;
  question: string;
  onDismiss: () => void;
  onTrigger: () => void;
}

export const DEFAULT_EXIT_INTENT_QUESTION =
  '👋 Hey, is there anything I could help you find?';

/**
 * Hook that detects exit intent via mouseleave events.
 * Uses sessionStorage for once-per-session persistence.
 * Desktop only - mouse leaving toward top of viewport.
 */
export const useExitIntent = ({
  enabled = false,
  question,
  onExitIntent,
}: UseExitIntentOptions): UseExitIntentReturn => {
  const { isFeatureEnabled } = useUserContext();
  const isFlagEnabled = isFeatureEnabled(Featureflag.EXIT_INTENT);
  // Use default if question is undefined or empty string
  const resolvedQuestion = question || DEFAULT_EXIT_INTENT_QUESTION;
  const { trackEvent } = useAnalytics();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [isTriggered, setIsTriggered] = useState(false);
  const hasTriggeredRef = useRef(hasShownThisSession());

  const onExitIntentRef = useRef(onExitIntent);
  onExitIntentRef.current = onExitIntent;

  const onTrigger = useCallback(() => {
    if (!hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      markAsShown();
      setIsTriggered(true);
      trackEvent(AnalyticsEvent.SUPERBAR_EXIT_INTENT_SHOWN);
      onExitIntentRef.current?.();
    }
  }, [trackEvent]);

  useEffect(() => {
    if (hasTriggeredRef.current || !enabled || !isFlagEnabled || isMobile) {
      return;
    }

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        onTrigger();
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [enabled, isFlagEnabled, isMobile, onTrigger]);

  const onDismiss = useCallback(() => {
    setIsTriggered(false);
  }, []);

  return {
    isTriggered,
    question: resolvedQuestion,
    onDismiss,
    onTrigger,
  };
};
